import { CheckCheck, Gavel, History, Info, Lock, RefreshCw, Unlock } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import {
  api,
  DISPUTE_APPROVAL_RESOURCE,
  isApiError,
  LEGAL_UNLOCK_ACTION,
  type ApprovalDecisionRequest,
  type ApprovalRequest,
  type ApprovalStatus,
  type Dispute,
  type DisputeCloseRequest,
  type DisputeDecision,
  type DisputeDecisionRequest,
  type NewApproval,
} from '@/api';
import { Badge } from '@/components/Badge';
import { Callout, Field, Section } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { PlainId } from '@/components/Ids';
import { SystemReason } from '@/components/SystemReason';
import { KeyValue } from '@/components/KeyValue';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { CopyText, Loading } from '@/components/States';
import { useRetained, useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime, formatRelative } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { ActionDialog, ConfirmCheck, EnumBadge, FormActions, MfaHint, SelectField, SuccessNote, Timeline, useEnumLabel, useEnumOptions } from './ui';
import { DISPUTE_DECISIONS, DISPUTE_STATUS } from './vocab';

type Dialogs = 'decide' | 'implement' | 'close' | null;
const TABS = ['summary', 'actions', 'history'] as const;
type Tab = (typeof TABS)[number];

function DecideForm({ d, onDone, onCancel }: { d: Dispute; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const options = useEnumOptions();
  const [decision, setDecision] = useState<DisputeDecision>('DENIED');
  const [summary, setSummary] = useState('');
  const [confirm, setConfirm] = useState(false);
  const write = useWrite((req: DisputeDecisionRequest, opts) => api.decideDispute(d.id, req, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (
          confirm &&
          (await write.run({
            decision,
            decision_summary: summary.trim() || null,
          }))
        )
          onDone();
      }}
    >
      <SelectField
        label={t('disputes.decide.decision')}
        value={decision}
        options={options('disputeDecision', DISPUTE_DECISIONS)}
        onChange={(v) => v && setDecision(v)}
        disabled={write.busy}
      />
      <Field label={t('disputes.decide.summary')}>
        <Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} disabled={write.busy} />
      </Field>
      <ConfirmCheck checked={confirm} onChange={setConfirm} disabled={write.busy}>
        {t('disputes.decide.confirm')}
      </ConfirmCheck>
      <MfaHint permission="dispute.manage" />
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !confirm}>
          {write.busy ? t('common.saving') : t('disputes.decide.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

function ImplementForm({ d, onDone, onCancel }: { d: Dispute; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const [note, setNote] = useState('');
  const write = useWrite((n: string, opts) => api.markDisputeImplemented(d.id, n, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (note.trim() && (await write.run(note.trim()))) onDone();
      }}
    >
      <Field label={t('disputes.implement.note')}>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} disabled={write.busy} />
      </Field>
      <MfaHint permission="dispute.manage" />
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !note.trim()}>
          {write.busy ? t('common.saving') : t('disputes.implement.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

function Step({ n, title, children, done }: { n: number; title: ReactNode; children: ReactNode; done?: boolean }) {
  return (
    <li className="relative grid grid-cols-[1.75rem_1fr] gap-3">
      <span
        className={
          done
            ? 'flex size-7 items-center justify-center rounded-full bg-success text-xs font-semibold text-white'
            : 'flex size-7 items-center justify-center rounded-full border-2 border-primary text-xs font-semibold text-primary'
        }
        aria-hidden="true"
      >
        {n}
      </span>
      <div className="min-w-0 space-y-2 pb-1">
        <p className="pt-1 text-sm font-semibold">{title}</p>
        {children}
      </div>
    </li>
  );
}

/** Four-eyes: request, then a different operator approves, then close with the approval ID. */
function CloseFlow({ d, onDone, known, remember }: { d: Dispute; onDone: () => void; known: Map<string, ApprovalStatus>; remember: (id: string, s: ApprovalStatus) => void }) {
  const { t } = useTranslation('compliance');
  const label = useEnumLabel();
  const options = useEnumOptions();
  const outcomes: DisputeCloseRequest['outcome'][] = d.status === 'OPENED' || d.status === 'LOCKED' ? ['WITHDRAWN'] : ['CLOSED'];
  const approvals = useApi((signal) => api.listApprovals({ signal }), []);
  const pending =
    approvals.status === 'success'
      ? approvals.data.items.filter((a) => a.action === LEGAL_UNLOCK_ACTION && a.resource_type === DISPUTE_APPROVAL_RESOURCE && a.resource_id === d.id)
      : [];
  const approved = [...known.entries()].filter(([, s]) => s === 'APPROVED').map(([k]) => k);
  const [outcome, setOutcome] = useState(outcomes[0] ?? 'CLOSED');
  const [approvalId, setApprovalId] = useState(approved[approved.length - 1] ?? '');
  const [reason, setReason] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [confirm, setConfirm] = useState(false);

  const request = useWrite((req: NewApproval, opts) => api.createApproval(req, opts));
  const close = useWrite((req: DisputeCloseRequest, opts) => api.closeDispute(d.id, req, opts));

  const submitRequest = async (e: FormEvent) => {
    e.preventDefault();
    if (!requestReason.trim()) return;
    const res = await request.run({
      action: LEGAL_UNLOCK_ACTION,
      resource_type: DISPUTE_APPROVAL_RESOURCE,
      resource_id: d.id,
      payload: { outcome, lock_id: d.lock_id, lock_type: d.lock_type },
      reason: requestReason.trim(),
    });
    if (res) {
      remember(res.id, res.status);
      approvals.reload();
    }
  };

  const submitClose = async (e: FormEvent) => {
    e.preventDefault();
    if (!approvalId.trim() || !reason.trim() || !confirm) return;
    if (
      await close.run({
        outcome,
        approval_request_id: approvalId.trim(),
        reason: reason.trim(),
      })
    )
      onDone();
  };

  const domain = d.domain ?? d.domain_id;

  return (
    <ol className="grid gap-5">
      <Step n={1} title={t('disputes.close.step1')} done={known.size > 0 || pending.length > 0}>
        <p className="text-sm text-muted-foreground">
          {t('disputes.close.step1Body')} <code className="rounded bg-muted px-1 text-xs">{LEGAL_UNLOCK_ACTION}</code>
        </p>
        <form className="flex flex-col gap-2 sm:flex-row" onSubmit={submitRequest}>
          <Input
            aria-label={t('disputes.close.requestReason')}
            value={requestReason}
            onChange={(e) => setRequestReason(e.target.value)}
            placeholder={t('disputes.close.requestReason')}
            disabled={request.busy}
          />
          <Button type="submit" variant="outline" disabled={request.busy || !requestReason.trim()}>
            {request.busy ? t('disputes.close.requestBusy') : t('disputes.close.requestSubmit')}
          </Button>
        </form>
        {request.state.phase === 'error' ? <WriteError error={request.state.error} /> : null}
        {request.state.phase === 'done' ? (
          <SuccessNote>
            {t('disputes.close.requested')} <CopyText value={request.state.result.id} /> {t('disputes.close.requestedHint')}
          </SuccessNote>
        ) : null}
      </Step>
      <Step n={2} title={t('disputes.close.step2')} done={approved.length > 0}>
        <p className="text-sm text-muted-foreground">{t('disputes.close.step2Body')}</p>
        {approvals.status === 'loading' ? <Loading /> : null}
        {approvals.status === 'error' ? <DataError error={approvals.error} onRetry={approvals.reload} /> : null}
        {pending.length === 0 && approvals.status === 'success' ? <p className="text-sm text-muted-foreground">{t('disputes.close.noPending')}</p> : null}
        {pending.map((a) => (
          <ApprovalDecision
            key={a.id}
            a={a}
            onDecided={(s) => {
              remember(a.id, s);
              if (s === 'APPROVED') setApprovalId(a.id);
              approvals.reload();
            }}
          />
        ))}
        {[...known.entries()]
          .filter(([k]) => !pending.some((p) => p.id === k))
          .map(([k, s]) => (
            // An approval request has no name; its ID is what step 3 asks for, so keep it copyable.
            <p key={k} className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t('disputes.close.approvalId')}</span>
              <PlainId id={k} />
              <Badge tone={s === 'APPROVED' ? 'ok' : s === 'PENDING' ? 'pending' : 'neutral'} title={s}>
                {label('approvalStatus', s)}
              </Badge>
            </p>
          ))}
      </Step>
      <Step n={3} title={t('disputes.close.step3')}>
        <form className="grid gap-4" onSubmit={submitClose}>
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label={t('disputes.close.outcome')}
              value={outcome}
              options={options('closeOutcome', outcomes)}
              onChange={(v) => v && setOutcome(v)}
              disabled={close.busy}
            />
            <Field label={t('disputes.close.approvalId')}>
              <Input className="font-mono" value={approvalId} onChange={(e) => setApprovalId(e.target.value)} disabled={close.busy} />
            </Field>
            <Field label={t('disputes.close.reason')} className="sm:col-span-2">
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={close.busy} />
            </Field>
          </div>
          <ConfirmCheck checked={confirm} onChange={setConfirm} disabled={close.busy}>
            {t('disputes.close.confirm', { domain })}
          </ConfirmCheck>
          <MfaHint permission="dispute.manage" />
          {close.state.phase === 'error' ? <WriteError error={close.state.error} /> : null}
          <div>
            <Button type="submit" variant="destructive" disabled={close.busy || !approvalId.trim() || !reason.trim() || !confirm}>
              <Unlock /> {close.busy ? t('disputes.close.busy') : outcome === 'WITHDRAWN' ? t('disputes.close.submitWithdraw') : t('disputes.close.submitClose')}
            </Button>
          </div>
        </form>
      </Step>
    </ol>
  );
}

function ApprovalDecision({ a, onDecided }: { a: ApprovalRequest; onDecided: (s: ApprovalStatus) => void }) {
  const { t } = useTranslation('compliance');
  const [reason, setReason] = useState('');
  const write = useWrite((req: ApprovalDecisionRequest, opts) => api.decideApproval(a.id, req, opts));
  const decide = async (approve: boolean) => {
    const res = await write.run({ approve, reason: reason.trim() });
    if (res) onDecided(res.status);
  };
  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <p className="text-sm">
        {t('disputes.close.requestedBy')} <EntityRef label={a.requested_by_name} id={a.requested_by} /> {formatRelative(a.requested_at)},{' '}
        {t('disputes.close.expires', { when: formatRelative(a.expires_at) })}: “{a.reason}”
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          aria-label={t('disputes.close.decisionReason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('disputes.close.decisionReason')}
          disabled={write.busy}
        />
        <div className="flex gap-2">
          <Button type="button" onClick={() => decide(true)} disabled={write.busy || !reason.trim()}>
            {t('disputes.close.approve')}
          </Button>
          <Button type="button" variant="outline" onClick={() => decide(false)} disabled={write.busy || !reason.trim()}>
            {t('disputes.close.reject')}
          </Button>
        </div>
      </div>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
    </div>
  );
}

/** `/compliance/icann/disputes/:id` */
export function DisputePage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const result = useApi((signal) => api.getDispute(id, { signal }), [id]);
  const d = useRetained(result.status === 'success' ? result.data : undefined);
  const [dialog, setDialog] = useState<Dialogs>(null);
  const [known, setKnown] = useState<Map<string, ApprovalStatus>>(new Map());
  const rawTab = params.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(rawTab ?? '') ? (rawTab as Tab) : 'summary';
  useDocumentTitle(d ? `${t('disputes.detail.title')} ${d.case_reference}` : t('disputes.detail.title'));
  const crumbs = [
    { label: t('crumbs.compliance') },
    { label: t('crumbs.icann') },
    { label: t('disputes.list.title'), to: '/compliance/icann/disputes' },
    { label: d?.case_reference ?? '…' },
  ];

  if (!d) {
    return (
      <>
        <PageHeader title={t('disputes.detail.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError error={result.error} onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload} title={t('disputes.detail.loadError')} />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }

  const done = () => {
    setDialog(null);
    result.reload();
  };
  const setTab = (next: string) => {
    const p = new URLSearchParams(params);
    if (next === 'summary') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
  };
  const finished = d.status === 'CLOSED' || d.status === 'WITHDRAWN';
  const withdraw = d.status === 'OPENED' || d.status === 'LOCKED';
  const lockActive = d.lock ? !d.lock.released_at : false;

  const history = [
    {
      key: 'opened',
      at: d.opened_at,
      title: t('disputes.detail.history.opened'),
      tone: 'info' as const,
    },
    d.lock
      ? {
          key: 'lock',
          at: d.lock.applied_at,
          title: t('disputes.detail.history.lockApplied'),
          tone: 'warn' as const,
          meta: <SystemReason reason={d.lock.reason} />,
        }
      : null,
    d.decided_at && d.decision
      ? {
          key: 'decided',
          at: d.decided_at,
          title: t('disputes.detail.history.decided', {
            decision: label('disputeDecision', d.decision),
          }),
          tone: 'pending' as const,
          meta: d.decision_summary,
        }
      : null,
    d.implemented_at
      ? {
          key: 'impl',
          at: d.implemented_at,
          title: t('disputes.detail.history.implemented'),
          tone: 'pending' as const,
        }
      : null,
    d.lock?.released_at
      ? {
          key: 'release',
          at: d.lock.released_at,
          title: t('disputes.detail.history.lockReleased'),
          tone: 'ok' as const,
          meta: d.lock.release_reason ? <SystemReason reason={d.lock.release_reason} /> : null,
        }
      : null,
    d.closed_at
      ? {
          key: 'closed',
          at: d.closed_at,
          title: t('disputes.detail.history.closed'),
          tone: 'ok' as const,
          meta: d.close_reason,
        }
      : null,
  ]
    .filter((x) => x !== null)
    .sort((a, b) => (b.at ?? '').localeCompare(a.at ?? ''));

  return (
    <>
      <PageHeader
        title={
          <>
            <span title={d.dispute_type}>{label('disputeType', d.dispute_type)}</span> <span className="font-mono">{d.case_reference}</span>
          </>
        }
        crumbs={crumbs}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <EnumBadge group="disputeStatus" value={d.status} tones={DISPUTE_STATUS} />
            {d.lock ? (
              <Badge tone={lockActive ? 'warn' : 'neutral'} title={d.lock.lock_type}>
                <Lock /> {label('lockType', d.lock.lock_type)} · {lockActive ? t('disputes.list.lockActive') : t('disputes.list.lockReleased')}
              </Badge>
            ) : null}
            <span>{d.provider}</span>
            <span aria-hidden="true">·</span>
            <EntityRef label={d.domain} to={d.domain ? `/domains/${encodeURIComponent(d.domain)}` : undefined} id={d.domain_id} />
          </span>
        }
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="summary">
            <Info /> {t('common.tabs.summary')}
          </TabsTrigger>
          <TabsTrigger value="actions">
            <Gavel /> {t('common.tabs.actions')}
          </TabsTrigger>
          <TabsTrigger value="history">
            <History /> {t('common.tabs.history')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-2 space-y-4">
          <Section title={t('disputes.detail.facts')}>
            <KeyValue
              columns={3}
              rows={[
                [t('disputes.detail.opened'), `${formatDateTime(d.opened_at)} (${formatRelative(d.opened_at)})`],
                [t('disputes.detail.decision'), d.decision ? `${label('disputeDecision', d.decision)} · ${formatDateTime(d.decided_at)}` : '—'],
                [t('disputes.detail.decisionSummary'), d.decision_summary ?? '—'],
                [t('disputes.detail.implemented'), formatDateTime(d.implemented_at)],
                [t('disputes.detail.closed'), formatDateTime(d.closed_at)],
                [t('disputes.detail.closeReason'), d.close_reason ?? '—'],
                [t('disputes.detail.releaseApproval'), <PlainId key="ra" id={d.release_approval_request_id} />],
                [
                  t('disputes.detail.abuseCase'),
                  d.abuse_case_id ? (
                    <EntityRef
                      key="ac"
                      label={d.abuse_case?.case_number}
                      to={`/compliance/icann/abuse/${encodeURIComponent(d.abuse_case_id)}`}
                      id={d.abuse_case_id}
                      sub={d.abuse_case ? label('abuseStatus', d.abuse_case.status) : undefined}
                    />
                  ) : (
                    '—'
                  ),
                ],
                [t('disputes.detail.summary'), d.summary ?? '—'],
              ]}
            />
          </Section>
          <Section
            title={t('disputes.detail.lock')}
            actions={
              d.lock_id ? (
                <Button asChild size="sm" variant="ghost">
                  <Link to={`/compliance/legal-locks/${encodeURIComponent(d.lock_id)}`}>{t('common.details')}</Link>
                </Button>
              ) : null
            }
          >
            {d.lock ? (
              <KeyValue
                columns={2}
                rows={[
                  [
                    t('disputes.detail.lockLabel'),
                    <EntityRef
                      key="l"
                      label={label('lockType', d.lock.lock_type)}
                      to={`/compliance/legal-locks/${encodeURIComponent(d.lock.id)}`}
                      id={d.lock.id}
                      sub={d.lock.released_at ? t('disputes.list.lockReleased') : t('disputes.list.lockActive')}
                    />,
                  ],
                  [t('disputes.detail.applied'), formatDateTime(d.lock.applied_at)],
                  [t('disputes.detail.reason'), <SystemReason key="lr" reason={d.lock.reason} />],
                  [
                    t('disputes.detail.released'),
                    d.lock.released_at ? (
                      <span key="rl">
                        {formatDateTime(d.lock.released_at)}: <SystemReason reason={d.lock.release_reason} />
                      </span>
                    ) : (
                      '—'
                    ),
                  ],
                ]}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{t('disputes.detail.noLock')}</p>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="actions" className="mt-2 space-y-4">
          {finished ? (
            <Callout tone="ok">{t('disputes.detail.finished')}</Callout>
          ) : (
            <Section title={t('disputes.detail.steps')}>
              <div className="grid gap-3 md:grid-cols-3">
                <ActionCard
                  icon={<Gavel />}
                  title={t('disputes.detail.decide')}
                  hint={t('disputes.detail.decideHint')}
                  enabled={d.status === 'LOCKED'}
                  onClick={() => setDialog('decide')}
                />
                <ActionCard
                  icon={<CheckCheck />}
                  title={t('disputes.detail.implement')}
                  hint={t('disputes.detail.implementHint')}
                  enabled={d.status === 'DECIDED'}
                  onClick={() => setDialog('implement')}
                />
                <ActionCard
                  icon={<Unlock />}
                  title={withdraw ? t('disputes.detail.withdraw') : t('disputes.detail.close')}
                  hint={t('disputes.detail.closeHint')}
                  enabled
                  destructive
                  onClick={() => setDialog('close')}
                />
              </div>
              <MfaHint permission="dispute.manage" className="mt-4" />
            </Section>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-2">
          <Section title={t('common.tabs.history')}>
            <Timeline
              items={history.map((h) => ({
                key: h.key,
                at: h.at,
                title: <span className="font-medium">{h.title}</span>,
                meta: 'meta' in h && h.meta ? h.meta : undefined,
                tone: h.tone,
              }))}
            />
          </Section>
        </TabsContent>
      </Tabs>

      <ActionDialog
        open={dialog === 'decide'}
        onOpenChange={(o) => setDialog(o ? 'decide' : null)}
        title={t('disputes.decide.title')}
        description={t('disputes.decide.description')}
      >
        <DecideForm d={d} onDone={done} onCancel={() => setDialog(null)} />
      </ActionDialog>
      <ActionDialog open={dialog === 'implement'} onOpenChange={(o) => setDialog(o ? 'implement' : null)} title={t('disputes.implement.title')}>
        <ImplementForm d={d} onDone={done} onCancel={() => setDialog(null)} />
      </ActionDialog>
      <ActionDialog
        open={dialog === 'close'}
        onOpenChange={(o) => setDialog(o ? 'close' : null)}
        title={withdraw ? t('disputes.close.titleWithdraw') : t('disputes.close.titleClose')}
        description={t('disputes.close.description')}
        wide
      >
        <CloseFlow d={d} onDone={done} known={known} remember={(k, s) => setKnown((m) => new Map(m).set(k, s))} />
      </ActionDialog>
    </>
  );
}

function ActionCard({
  icon,
  title,
  hint,
  enabled,
  destructive,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  hint: string;
  enabled: boolean;
  destructive?: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="space-y-1">
        <p className="text-sm font-semibold">{title.replace(/…$/, '')}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Button className="mt-auto w-fit" size="sm" variant={destructive ? 'destructive' : 'outline'} disabled={!enabled} onClick={onClick}>
        {icon} {title}
      </Button>
    </div>
  );
}
